/**
 * Small, accessible control primitives for the Builder editor panels. Presentation only
 * — they hold no editor state; the parent panel wires them to the editor context.
 */
import { useId, useRef, useState } from 'react'
import { ChevronRight, ArrowLeft, Upload, Trash2, Star } from 'lucide-react'
import { BRAND } from '../../config'
import { readImageFile } from './editorContext'

/** A titled group of controls. */
export function Fieldset({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="border-t border-gray-100 px-5 py-5 first:border-t-0">
      <legend className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400">{title}</legend>
      <div className="flex flex-col gap-4">{children}</div>
    </fieldset>
  )
}

/** A labeled row wrapper for a single control. */
function Row({ label, htmlFor, children }: { label: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-xs font-medium text-gray-600">
        {label}
      </label>
      {children}
    </div>
  )
}

/** Segmented single-choice control (design system / radius / spacing). */
export function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T
  options: readonly { value: T; label: string }[]
  onChange: (v: T) => void
}) {
  return (
    <Row label={label}>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1 rounded-xl bg-gray-100 p-1">
        {options.map((o) => {
          const active = o.value === value
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(o.value)}
              className={`flex-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
                active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {o.label}
            </button>
          )
        })}
      </div>
    </Row>
  )
}

/** Color control: native swatch + a synced hex text input. */
export function ColorField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (hex: string) => void
}) {
  const id = useId()
  const normalized = /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'
  return (
    <Row label={label} htmlFor={id}>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="color"
          value={normalized}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-10 shrink-0 cursor-pointer rounded-lg border border-gray-200 bg-white p-0.5"
          aria-label={`${label} color picker`}
        />
        <input
          type="text"
          value={value}
          spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-gray-200 px-2.5 py-2 font-mono text-xs uppercase text-gray-800 focus:border-gray-400 focus:outline-none"
          aria-label={`${label} hex value`}
        />
      </div>
    </Row>
  )
}

/** A bounded dropdown (e.g. fonts). */
export function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T
  options: readonly T[]
  onChange: (v: T) => void
}) {
  const id = useId()
  return (
    <Row label={label} htmlFor={id}>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-800 focus:border-gray-400 focus:outline-none"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </Row>
  )
}

/** Single-line text input. */
export function TextField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  const id = useId()
  return (
    <Row label={label} htmlFor={id}>
      <input
        id={id}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-200 px-2.5 py-2 text-sm text-gray-800 focus:border-gray-400 focus:outline-none"
      />
    </Row>
  )
}

/** Multi-line text input. */
export function TextArea({
  label,
  value,
  onChange,
  rows = 3,
  maxLength,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  rows?: number
  maxLength?: number
}) {
  const id = useId()
  return (
    <Row label={label} htmlFor={id}>
      <textarea
        id={id}
        value={value}
        rows={rows}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        className="w-full resize-y rounded-lg border border-gray-200 px-2.5 py-2 text-sm text-gray-800 focus:border-gray-400 focus:outline-none"
      />
      {maxLength ? <span className="text-right text-[10px] text-gray-400">{value.length}/{maxLength}</span> : null}
    </Row>
  )
}

/** A labeled on/off switch (e.g. section visibility). */
export function ToggleField({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description?: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex flex-col">
        <span className="text-xs font-medium text-gray-700">{label}</span>
        {description ? <span className="text-[11px] text-gray-400">{description}</span> : null}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? 'bg-green-500' : 'bg-gray-200'}`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  )
}

/**
 * A 1–5 clickable star rating, matching the reference review card's own star display
 * (`reviews.css`: unfilled `#e4e5e9`, active `#ffc107`) rather than a raw number input.
 */
export function StarRatingField({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (v: number) => void
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <div className="flex items-center gap-1" role="radiogroup" aria-label={label}>
        {[1, 2, 3, 4, 5].map((star) => {
          const active = star <= value
          return (
            <button
              key={star}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={`${star} star${star > 1 ? 's' : ''}`}
              onClick={() => onChange(star)}
              className="p-0.5"
            >
              <Star
                className="h-5 w-5"
                style={{ color: active ? '#ffc107' : '#e4e5e9' }}
                fill={active ? '#ffc107' : 'none'}
              />
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** A range slider with a live value readout. */
export function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  suffix,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step?: number
  suffix?: string
  onChange: (v: number) => void
}) {
  const id = useId()
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-xs font-medium text-gray-600">{label}</label>
        <span className="text-xs font-semibold text-gray-800">{value}{suffix}</span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-gray-900"
      />
    </div>
  )
}

/** Image upload with a preview thumbnail, backed by a data URL (no storage backend). */
export function ImageUploadField({
  label,
  value,
  onChange,
}: {
  label: string
  value?: string
  onChange: (src: string | null) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)

  const pick = (file: File | undefined) => {
    if (!file) return
    setError(null)
    readImageFile(file)
      .then(onChange)
      .catch((err: Error) => setError(err.message))
  }

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
          {value ? (
            <img src={value} alt={`${label} preview`} className="max-h-full max-w-full object-contain" />
          ) : (
            <Upload className="h-4 w-4 text-gray-300" />
          )}
        </div>
        <div className="flex flex-1 items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
          >
            {value ? 'Replace' : 'Upload'}
          </button>
          {value ? (
            <button
              type="button"
              onClick={() => { onChange(null); setError(null) }}
              className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-gray-500 transition-colors hover:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { pick(e.target.files?.[0]); e.target.value = '' }}
        />
      </div>
      {error ? <span className="text-[11px] text-red-600">{error}</span> : null}
    </div>
  )
}

/** A back header for a drilled-in editor screen. */
export function ScreenHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2.5">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <span className="text-sm font-semibold text-gray-900">{title}</span>
    </div>
  )
}

/** A vertical menu of navigable rows (the scalable sidebar pattern). */
export function MenuList({ children }: { children: React.ReactNode }) {
  return <ul className="flex flex-col p-2">{children}</ul>
}

export function MenuItem({
  label,
  description,
  icon: Icon,
  onClick,
}: {
  label: string
  description?: string
  icon?: typeof ChevronRight
  onClick: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-gray-100"
      >
        {Icon ? <Icon className="h-4 w-4 text-gray-500" /> : null}
        <span className="flex-1">
          <span className="block text-sm font-medium text-gray-900">{label}</span>
          {description ? <span className="block text-xs text-gray-500">{description}</span> : null}
        </span>
        <ChevronRight className="h-4 w-4 text-gray-400" />
      </button>
    </li>
  )
}

/** A read-only label/value row for immutable builder facts. */
export function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="truncate text-xs font-medium text-gray-800" title={value}>
        {value}
      </span>
    </div>
  )
}

export { BRAND }
